import React, { useState } from 'react';
import styled from 'styled-components';
import { POSModal, POSButton } from 'components/POS';
import { useCart } from 'shared/CartContext';
import { PaymentOption } from './PaymentOption';
import { TierOptionChoice } from './TierOptionChoice';

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
`;

const SeatsSectionTitle = styled.span`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  text-align: left;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const SeatsRowContainer = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  padding-bottom: 10px;
`;

const SeatRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
  text-align: left;
  padding: 10px 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const SeatsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 200px;
`;

const SeatItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: ${(props) =>
    props.isLast
      ? 'none'
      : props.theme.name === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.08)'};
  padding-bottom: 10px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: auto;
  padding-top: 20px;
`;

export const SeatSelectionModal = ({
  isVisible,
  setIsVisible,
  onClose,
  seatIdKey,
  seatCategory,
}) => {
  const {
    selectedSeatObject,
    setSelectedSeatObject,
    reservedSeatPrefix,
    reservedSeatSection,
    bestSeats,
    updateTicketSeat,
  } = useCart();

  const [isCheckoutVisible, setIsCheckoutVisible] = useState(false);

  const handleCheckout = () => {
    setIsVisible(false);
    onClose();
  };

  const seatData = selectedSeatObject?.[seatIdKey] || {};
  const hasSeats = seatData && Object.keys(seatData).length > 0;

  return (
    <>
      <POSModal
        isVisible={isVisible}
        onClose={() => setIsVisible(false)}
        title="Your Selection"
      >
        <BodyWrapper>
          {hasSeats ? (
            <SeatsContainer>
              {Object.entries(seatData).map(
                ([sectionName, seatsInSection], sectionIndex) => (
                  <div key={sectionIndex}>
                    {reservedSeatSection && (
                      <div style={{ textAlign: 'start', marginTop: 20 }}>
                        <SeatsSectionTitle>
                          {`${reservedSeatPrefix ?? ''} ${sectionName}`}
                        </SeatsSectionTitle>
                      </div>
                    )}
                    <SeatsRowContainer>
                      {Array.isArray(seatsInSection) &&
                        seatsInSection.map((seat, seatIndex) => {
                          const isBestSeat = bestSeats.has(seat.id);

                          const category = seatCategory?.find(
                            (cat) => cat?.category === seat?.category?.label
                          );

                          const tierOptions =
                            category?.ticketTypes?.map((pricing) => ({
                              label: pricing.label,
                              value: pricing.price,
                              type: pricing.id,
                            })) || undefined;

                          const selectedTier = tierOptions?.find(
                            (pricing) => pricing.type === seat.type
                          );

                          const onTierSelected = (selectedOption) => {
                            const updatedSeat = {
                              ...seat,
                              price: selectedOption?.value,
                              type: selectedOption?.type,
                            };

                            setSelectedSeatObject((prev) => ({
                              ...prev,
                              [seatIdKey]: {
                                ...prev[seatIdKey],
                                [sectionName]: prev[seatIdKey][
                                  sectionName
                                ]?.map((s) =>
                                  s.id === seat.id ? updatedSeat : s
                                ),
                              },
                            }));

                            updateTicketSeat(updatedSeat);
                          };

                          return (
                            <SeatItemWrapper
                              key={seatIndex}
                              isLast={seatIndex + 1 === seatsInSection.length}
                            >
                              <SeatRow>
                                {reservedSeatSection ? (
                                  <>
                                    <span>
                                      Row: {seat.labels?.parent ?? '-'}
                                    </span>
                                    <span>Seat: {seat.labels?.own ?? '-'}</span>
                                  </>
                                ) : (
                                  <span>
                                    {`${
                                      reservedSeatPrefix ?? ''
                                    } ${sectionName}`}{' '}
                                    {seat.labels?.own ?? ''}
                                  </span>
                                )}
                              </SeatRow>
                              {tierOptions?.length > 0 && isBestSeat && (
                                <TierOptionChoice
                                  tierOptions={tierOptions}
                                  selectedTier={selectedTier ?? tierOptions[0]}
                                  onTierSelected={onTierSelected}
                                />
                              )}
                            </SeatItemWrapper>
                          );
                        })}
                    </SeatsRowContainer>
                  </div>
                )
              )}
            </SeatsContainer>
          ) : (
            <EmptyState>
              <SeatsSectionTitle style={{ textAlign: 'center' }}>
                Seats not selected
              </SeatsSectionTitle>
            </EmptyState>
          )}

          {/* Action Buttons */}
          <ButtonWrapper>
            <POSButton
              variant="outlined"
              style={{
                flex: 1,
                padding: '16px 20px',
                fontSize: '18px',
              }}
              onClick={() => setIsVisible(false)}
            >
              Cancel
            </POSButton>
            <POSButton
              variant="primary"
              style={{
                flex: 1,
                padding: '16px 20px',
                fontSize: '18px',
              }}
              disabled={!hasSeats}
              onClick={handleCheckout}
            >
              Confirm
            </POSButton>
          </ButtonWrapper>
        </BodyWrapper>
      </POSModal>

      {/* Payment Modal */}
      <PaymentOption
        isVisible={isCheckoutVisible}
        setIsVisible={setIsCheckoutVisible}
      />
    </>
  );
};
